import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import cn from 'classnames';

import { ESC } from '../constants/keyCodes';
import getField from '../utils/getField';
import toggleScroll from '../utils/toggleScroll';
import Dialog from './Dialog';
import Portal from '../Helpers/Portal';

/**
 * The `DialogContainer` component is used for dynamically creating the `Dialog` with
 * transitions.
 */
export default class DialogContainer extends PureComponent {

  static defaultProps = {
    autopadContent: true,
    autosizeContent: true,
    component: 'span',
    closeOnEsc: true,
    contentComponent: 'section',
    focusOnMount: true,
    transitionEnterTimeout: 300,
    transitionLeaveTimeout: 300,
    defaultVisibleTransitionable: false,
    activeElementFocus: true,
  };

  static contextTypes = {
    renderNode: PropTypes.object,
  };

  constructor(props) {
    super(props);

    const visible = typeof props.isOpen !== 'undefined' ? props.isOpen : props.visible;
    const dialogVisible = visible && !props.defaultVisibleTransitionable;

    this.state = {
      active: visible && !props.fullPage,
      portalVisible: visible,
      dialogVisible,
    };
  }

  componentDidMount() {
    if (!this.props.isOpen && !this.props.visible) {
      return;
    }

    this._mountDialog(this.props);
  }

  componentWillReceiveProps(nextProps) {
    const visible = typeof nextProps.isOpen !== 'undefined' ? nextProps.isOpen : nextProps.visible;
    if (this.props.isOpen === visible || this.props.visible === visible) {
      return;
    }

    const el = getField(this.props, this.context, 'renderNode') || window;
    let { scrollX: pageX, scrollY: pageY } = el;
    if (typeof el.scrollTop !== 'undefined' && typeof el.scrollLeft !== 'undefined') {
      pageX = el.scrollLeft;
      pageY = el.scrollTop;
    } else if (typeof el.scrollY !== 'undefined' && typeof el.scrollX !== 'undefined') {
      pageX = el.scrollX;
      pageY = el.scrollY;
    }

    this._pageX = pageX;
    this._pageY = pageY;

    if (this._inTimeout) {
      clearTimeout(this._inTimeout);
      this._inTimeout = null;
    }

    if (visible) {
      this._activeElement = document.activeElement;
      this._mountPortal(nextProps);
    } else {
      this.setState({ dialogVisible: false, active: false });
    }
  }

  componentDidUpdate(prevProps) {
    const { visible, closeOnEsc } = this.props;

    // Only going to support visible here since it was not implemented before.
    if (visible === prevProps.visible && closeOnEsc === prevProps.closeOnEsc) {
      return;
    }

    let add = false;
    let remove = false;
    if (closeOnEsc !== prevProps.closeOnEsc) {
      add = visible && closeOnEsc;
      remove = !visible || (prevProps.closeOnEsc && !closeOnEsc);
    } else if (closeOnEsc) {
      add = visible;
      remove = !visible;
    }

    if (add) {
      window.addEventListener('keydown', this._handleEscClose);
    } else if (remove) {
      window.removeEventListener('keydown', this._handleEscClose);
    }
  }

  componentWillUnmount() {
    if (this.props.isOpen || this.props.visible) {
      toggleScroll(false);
    }

    if (this.props.visible && this.props.closeOnEsc) {
      window.removeEventListener('keydown', this._handleEscClose);
    }

    if (this._inTimeout) {
      clearTimeout(this._inTimeout);
    }
  }

  _setContainer = (container) => {
    if (container !== null) {
      this._container = findDOMNode(container);
    }
  };

  _handleEscClose = (e) => {
    if ((e.which || e.keyCode) === ESC) {
      (this.props.onHide || this.props.close)(e);
    }
  };

  _mountPortal = (props) => {
    this._mountDialog(props);
    this.setState({ portalVisible: true });
  };

  _mountDialog = (props) => {
    const { fullPage, onShow } = props;
    this._inTimeout = setTimeout(() => {
      this._inTimeout = fullPage ? null : setTimeout(() => {
        this._inTimeout = null;
        this.setState({ active: true });
      }, 17);
      this.setState({ dialogVisible: true }, onShow);
    }, 17);
  };

  _unmountPortal = () => {
    this.setState({ portalVisible: false });
  };

  _handleClick = (e) => {
    const visible = typeof this.props.isOpen !== 'undefined' ? this.props.isOpen : this.props.visible;
    if (this.props.modal || !visible || e.target !== this._container) {
      return;
    }

    (this.props.onHide || this.props.close)(e);
  };

  _handleDialogMounting = (dialog) => {
    const { disableScrollLocking } = this.props;
    if (dialog === null) {
      if (this._activeElement && this._activeElement.focus && this.props.activeElementFocus) {
        this._activeElement.focus();
      }

      if (!disableScrollLocking) {
        toggleScroll(false, this.scrollEl);
      }

      this._activeElement = null;
    } else {
      const container = document.getElementById(this.props.id);
      if (!container || disableScrollLocking) {
        return;
      }

      let el = getField(this.props, this.context, 'renderNode');
      let node = container.parentNode;
      while (node && node.classList && !el) {
        if (node.classList.contains('md-dialog')) {
          el = node;
        }

        node = node.parentNode;
      }

      this.scrollEl = el;
      toggleScroll(true, el);
    }
  };

  render() {
    const { active, dialogVisible, portalVisible } = this.state;
    const {
      style,
      className,
      dialogStyle,
      dialogClassName,
      modal,
      fullPage,
      component,
      transitionEnterTimeout,
      transitionLeaveTimeout,
      lastChild,
      portal,
      /* eslint-disable no-unused-vars */
      visible: propVisible,
      renderNode: propRenderNode,
      closeOnEsc,
      onShow,
      onHide,
      disableScrollLocking,
      defaultVisibleTransitionable,
      activeElementFocus,

      // deprecated
      close,
      isOpen,
      actionLeft,
      actionRight,
      transitionName,
      transitionEnter,
      transitionLeave,
      /* eslint-enable no-unused-vars */
      ...props
    } = this.props;

    const renderNode = getField(this.props, this.context, 'renderNode');
    const dialog = (
      <Dialog
        key="dialog"
        style={dialogStyle}
        className={cn('md-background--card', dialogClassName)}
        ref={this._handleDialogMounting}
        centered={!fullPage}
        fullPage={fullPage}
        {...props}
        containerX={this._pageX}
        containerY={this._pageY}
        onLeave={this._unmountPortal}
      />
    );

    const container = (
      <CSSTransitionGroup
        component={component}
        ref={this._setContainer}
        style={style}
        className={cn('md-dialog-container', {
          'md-overlay': !fullPage,
          'md-overlay--active': !fullPage && active && propVisible,
          'md-pointer--hover': !fullPage && !modal && propVisible,
        }, className)}
        transitionName={`md-dialog--${fullPage ? 'full-page' : 'centered'}`}
        transitionEnterTimeout={transitionEnterTimeout}
        transitionLeaveTimeout={transitionLeaveTimeout}
        tabIndex={-1}
        onClick={this._handleClick}
      >
        {dialogVisible ? dialog : null}
      </CSSTransitionGroup>
    );

    if (!portal) {
      return portalVisible ? container : null;
    }

    return (
      <Portal visible={portalVisible} renderNode={renderNode} lastChild={lastChild}>
        {container}
      </Portal>
    );
  }
}
